import { AUTHOR_NAME_MAX } from "@talkers/shared";
import { createSignal, type JSX, Show } from "solid-js";
import { AvatarBuilder } from "@/components/AvatarBuilder";
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  HStack,
  Stack,
  TextField,
} from "@/components/ui";
import { currentDisplay, isLoggedIn, me, saveDisplay } from "@/state/me";

/**
 * 名前とアバターの編集。
 *
 * 保存先はゲストでもログイン済みでも `PATCH /me`。まだセッションが無ければ、
 * 保存のときにゲストとして作られる。
 * 下書きは開いている間だけ持つ (呼び出し側が open のときだけマウントする)。
 */
export function IdentityDialog(props: { onClose: () => void }): JSX.Element {
  const [draft, setDraft] = createSignal(currentDisplay());
  const [error, setError] = createSignal<string | null>(null);
  const [saving, setSaving] = createSignal(false);

  const save = async () => {
    if (saving()) return;
    setSaving(true);
    setError(null);
    try {
      const name = draft().name.trim();
      await saveDisplay({
        ...draft(),
        name: name === "" ? currentDisplay().name : name,
      });
      props.onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "保存できませんでした");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={props.onClose} size="lg">
      <DialogHeader>あなたの猫</DialogHeader>
      <DialogBody>
        <Stack gap={4}>
          <Show when={error()}>{(message) => <Alert variant="danger">{message()}</Alert>}</Show>

          <Show
            when={isLoggedIn() ? me() : undefined}
            fallback={
              <Alert variant="info">
                この端末のゲストとして保存します。ログインすると、ここまでの投稿もそのまま引き継がれます。
              </Alert>
            }
          >
            {(user) => <p class="page-lead">{user().handle} として保存します。</p>}
          </Show>

          <HStack gap={2} align="end" wrap>
            <div class="identity-name-field">
              <TextField
                label="名前"
                value={draft().name}
                onInput={(name) => setDraft((current) => ({ ...current, name }))}
                hint={`${draft().name.length} / ${AUTHOR_NAME_MAX}`}
              />
            </div>
          </HStack>

          <AvatarBuilder
            value={draft().avatar}
            onChange={(avatar) => setDraft((current) => ({ ...current, avatar }))}
          />
        </Stack>
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={props.onClose}>
          やめる
        </Button>
        <Button variant="primary" loading={saving()} onClick={save}>
          保存する
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
